import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { pickBilingual, type Locale } from '@lims/shared-types';
import {
  sampleReceiptsApi,
  type SampleRequestedService,
  type SampleReceipt,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  ActionButton,
  DataTable,
  ErrorState,
  LoadingState,
  Panel,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatDateTime, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { ReceiptDocumentActions } from './ReceiptDocumentActions.js';
import { toneForSampleStatus } from './status.js';
import { WorkOrderContextCard } from './WorkOrderContextCard.js';
import { RecordHeader, recordLinkClass } from './RecordHeader.js';
import { SampleTypeName } from './sample-type-fields.js';

type ReceiptLine = SampleReceipt['samples'][number];

/**
 * One sample receipt: a numbered handover and the samples it brought (D193).
 *
 * READ-ONLY. A receipt records a handover that happened; the database refuses
 * to change it. Each line keeps its own Sample ID and its own acceptance
 * decision, taken separately by whoever may take it — the receipt shows the
 * decision, it does not make one.
 *
 * NO SIGNATURE IS CAPTURED. The paper form has signature lines for the person
 * who delivered and the person who received. The system records WHO received
 * (the signed-in user) and who delivered (as stated at the counter); it does not
 * record, draw or imply a signature, and says so on the page.
 *
 * PERMANENTLY REACHABLE (D213): from the Sample Receipts register, from every
 * sample on it and from the Samples list. Print Receipt and Download PDF are
 * the server's receipt document, rendered on each request and never stored.
 *
 * A VOIDED SAMPLE STAYS ON ITS RECEIPT, marked as voided (D211): the handover
 * happened, and the receipt says what it brought.
 *
 * EACH SAMPLE ONCE, WITH ITS OWN REQUESTED TESTS (D220): name and standard as
 * the work order froze them, no price. A sample whose tests were never
 * recorded says so — it is never shown the work order's whole list.
 */
export function SampleReceiptPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();
  const timeZone = useLaboratoryTimeZone();

  const receipt = useQuery({
    queryKey: ['sample-receipt', id],
    queryFn: () => sampleReceiptsApi.get(id as string),
  });

  const workOrderId = receipt.data?.workOrder?.id ?? null;
  const mayReceive = can(PERMISSIONS.SAMPLES_RECEIVE);
  const mayDecide = can(PERMISSIONS.SAMPLES_DECIDE);
  // The job's context is read with the permission to receive (D194).
  const context = useQuery({
    queryKey: ['receiving-context', workOrderId],
    queryFn: () => sampleReceiptsApi.workOrderContext(workOrderId as string),
    enabled: workOrderId !== null && mayReceive,
  });

  if (receipt.isLoading) return <LoadingState label={t('common.loading')} />;
  if (receipt.error || !receipt.data) {
    return <ErrorState error={receipt.error} onRetry={() => void receipt.refetch()} />;
  }
  const record = receipt.data;
  const moment = (iso: string) =>
    formatDateTime(iso, { locale, ...(timeZone ? { timezone: timeZone } : {}) });

  const columns: readonly DataColumn<ReceiptLine>[] = [
    {
      key: 'sampleNumber',
      header: t('receiving.columns.sampleNo'),
      render: (line) => (
        <div className="flex flex-col items-start gap-1">
          <Link
            to={`/samples/${line.id}`}
            className={`measure whitespace-nowrap font-semibold underline-offset-2 hover:text-primary-700 hover:underline ${
              line.isVoided ? 'text-default-600 line-through' : 'text-default-900'
            }`}
            dir="ltr"
          >
            {line.sampleNumber}
          </Link>
          {line.isVoided ? (
            <StatusBadge label={t('sampleVoid.voidedChip')} tone="danger" size="sm" />
          ) : null}
        </div>
      ),
    },
    {
      key: 'requestedTests',
      header: t('receipts.requestedTests'),
      render: (line) => <RequestedTests services={line.requestedServices} locale={locale} />,
    },
    /* Material and quantity share a cell, so the table fits a laptop without
       sideways scrolling (D278). */
    {
      key: 'sampleType',
      header: t('receipts.materialAndQuantity'),
      render: (line) => (
        <div className="flex flex-col">
          <span><SampleTypeName code={line.sampleType} /></span>
          <span className="text-caption text-default-600" dir="ltr">
            {formatNumber(line.quantity, { locale })} {line.quantityUnit}
          </span>
        </div>
      ),
    },
    {
      key: 'clientReference',
      header: t('receivingField.client_reference'),
      render: (line) => line.clientReference ?? t('common.notRecorded'),
    },
    {
      key: 'condition',
      header: t('receiving.columns.condition'),
      render: (line) => line.conditionOnReceipt ?? t('common.notRecorded'),
    },
    {
      key: 'decision',
      header: t('receipts.acceptanceStatus'),
      render: (line) => (
        <div className="flex flex-col items-start gap-1">
          <StatusBadge
            label={t(`status.sample.${line.status}`)}
            tone={toneForSampleStatus(line.status)}
          />
          {line.currentDecision?.reason ? (
            <span className="text-caption text-default-600">
              {t('receiving.reasonLabel')}: {line.currentDecision.reason}
            </span>
          ) : null}
          {/* The row's next step sits under its state rather than in a column
              of its own, so the table fits a laptop (D278). */}
          {rowAction(line)}
        </div>
      ),
    },
  ];
  function rowAction(line: (typeof record.samples)[number]) {
    return (
        // The next step where the sample file offers it: a sample awaiting
        // inspection opens with the decision dialog, to whoever may decide.
        mayDecide && !line.isVoided && line.status === 'awaiting_inspection' ? (
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() => navigate(`/samples/${line.id}?decide=1`)}
            aria-label={t('samples.inspectNamed', { number: line.sampleNumber })}
          >
            {t('samples.inspect')}
          </ActionButton>
        ) : (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => navigate(`/samples/${line.id}`)}
            aria-label={t('receipts.openSampleNamed', { number: line.sampleNumber })}
          >
            {t('receipts.openSample')}
          </ActionButton>
        )
    );
  }
  const location = record.location
    ? [record.location.name, record.location.address, record.location.city]
        .filter((part): part is string => Boolean(part))
        .join(' — ')
    : null;
  // Counted from the lines the server returned, never estimated.
  const awaiting = record.samples.filter(
    (line) => !line.isVoided && line.status === 'awaiting_inspection',
  ).length;
  const voided = record.samples.filter((line) => line.isVoided).length;

  const keyContext = [
    {
      key: 'workOrder',
      label: t('samples.jobNumber'),
      value: record.workOrder ? (
        <Link
          to={`/work-orders/${record.workOrder.id}`}
          className={`measure ${recordLinkClass}`}
          dir="ltr"
        >
          {record.workOrder.workOrderNumber}
        </Link>
      ) : (
        <span className="text-default-600">{t('samples.noWorkOrder')}</span>
      ),
    },
    {
      key: 'client',
      label: t('receiving.client'),
      value: record.client ? (
        can(PERMISSIONS.CLIENTS_VIEW) ? (
          <Link to={`/clients/${record.client.id}`} className={recordLinkClass}>
            {record.client.name}
          </Link>
        ) : (
          record.client.name
        )
      ) : (
        <span className="text-default-600">{t('common.notRecorded')}</span>
      ),
    },
    ...(record.project
      ? [
          {
            key: 'project',
            label: t('receiving.project'),
            value: can(PERMISSIONS.PROJECTS_VIEW) ? (
              <Link to={`/projects/${record.project.id}`} className={recordLinkClass}>
                {record.project.name}
              </Link>
            ) : (
              record.project.name
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <RecordHeader
        number={record.receiptNumber}
        title={t('receipts.recordTitle')}
        breadcrumbs={[
          { label: t('nav.samples'), to: '/samples' },
          { label: t('receipts.title'), to: '/samples/receipts' },
          { label: record.receiptNumber },
        ]}
        badges={
          voided > 0 ? (
            <StatusBadge label={t('receipts.voidedCount', { count: voided })} tone="warning" />
          ) : null
        }
        context={keyContext}
        waitingOn={awaiting > 0 ? t('receipts.awaitingOnReceipt', { count: awaiting }) : null}
        actions={
          mayReceive ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate(`/samples/receive?receiptId=${record.id}`)}
            >
              {t('receiving.anotherOnReceipt')}
            </ActionButton>
          ) : null
        }
      />
      <p className="-mt-2 mb-gutter text-body text-default-600">{t('receiving.receiptSubtitle')}</p>

      {/* The samples table needs the full width below 1536px; the handover facts then sit underneath it. */}
      <div className="grid grid-cols-1 items-start gap-gutter 2xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex min-w-0 flex-col gap-gutter">
          <Panel title={t('receiving.samplesOnReceipt')}>
            <DataTable
              label={t('receiving.samplesOnReceipt')}
              columns={columns}
              rows={record.samples}
              rowKey={(line) => line.id}
            />
            <p className="mt-stack text-caption text-default-600">
              {t('receiving.decisionIsSeparate')}
            </p>
          </Panel>
          {context.data ? <WorkOrderContextCard context={context.data} /> : null}
        </div>

        <aside className="grid min-w-0 grid-cols-1 items-start gap-gutter md:grid-cols-2 2xl:grid-cols-1">
          <Panel title={t('receiving.handover')}>
            <dl className="flex flex-col gap-2.5">
              <Fact label={t('receiving.receiptDateTime')}>{moment(record.receivedAt)}</Fact>
              {location ? <Fact label={t('receiving.location')}>{location}</Fact> : null}
              <Fact label={t('receipts.deliveredBy')}>{record.receivedFrom}</Fact>
              <Fact label={t('receiving.receivedBy')}>
                {record.receivedBy.displayName ?? t('common.notRecorded')}
              </Fact>
              <Fact label={t('receipts.sampleCount')}>
                <span className="measure" dir="ltr">
                  {formatNumber(record.samples.length, { locale })}
                </span>
              </Fact>
              {record.notes ? <Fact label={t('receiving.receiptNotes')}>{record.notes}</Fact> : null}
            </dl>
            <p className="mt-stack text-caption text-default-600">
              {t('receiving.signaturesNote')}
            </p>
          </Panel>
          {/* The server's receipt document — never this page (D213, D221). */}
          <Panel title={t('receipts.documentTitle')}>
            <ReceiptDocumentActions
              receiptId={record.id}
              receiptNumber={record.receiptNumber}
              size="sm"
            />
          </Panel>
        </aside>
      </div>
    </>
  );
}

/** One fact of the handover: a caption above its value, sized for a side column. */
function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-caption text-default-600">{label}</dt>
      <dd className="mt-0.5 break-words text-body text-default-900">{children}</dd>
    </div>
  );
}

/** "ASTM C136 (2019)": the standard and, when recorded, its version. */
function standardText(service: SampleRequestedService): string | null {
  const standard = service.referenceStandard?.trim();
  if (!standard) return null;
  const version = service.standardVersion?.trim();
  return version ? `${standard} (${version})` : standard;
}

/**
 * One sample's requested tests, inside its own row (D220): the name in the
 * interface language (the other one when only that was recorded) and the
 * standard. Nothing recorded reads "not recorded".
 */
function RequestedTests({
  services,
  locale,
}: {
  readonly services: readonly SampleRequestedService[];
  readonly locale: Locale;
}) {
  const { t } = useTranslation();
  if (services.length === 0) {
    return (
      <span className="text-default-600 italic">{t('receipts.requestedTestsNotRecorded')}</span>
    );
  }
  return (
    <ul className="flex min-w-[12rem] max-w-[20rem] flex-col gap-1" aria-label={t('receipts.requestedTests')}>
      {services.map((service) => {
        const name =
          pickBilingual(
            { ar: service.descriptionAr?.trim() || null, en: service.descriptionEn?.trim() || null },
            locale,
          ) ?? service.itemCode;
        const standard = standardText(service);
        return (
          <li key={service.workOrderServiceId} className="flex flex-col leading-snug">
            {name ? <bdi className="text-default-900">{name}</bdi> : null}
            {standard ? (
              <span className="text-caption text-default-600">
                <bdi dir="ltr">{standard}</bdi>
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
